import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@services/firebase";
import { USER_ROLES } from "@utils/constants";

/**
 * AuthContext — holds the current Firebase auth state and the user's role.
 *
 * This context ONLY wires the real-time listener and exposes the current
 * state. It intentionally does NOT implement sign-in, OTP verification, or
 * registration logic — that is the Phase 5 (Authentication System)
 * deliverable. `ProtectedRoute` and `AdminRoute` (see src/router) depend on
 * this context to guard routes from Phase 2 onward.
 */

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Firebase not configured (missing VITE_FIREBASE_* env) — treat as
    // signed-out instead of crashing the app.
    if (!auth) {
      setIsLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        // Role is stored as a Firebase Auth custom claim (set server-side
        // during the Phase 5/10 implementation). We read it here so route
        // guards have access to it as soon as auth state resolves.
        const tokenResult = await user.getIdTokenResult();
        setRole(tokenResult.claims.role ?? USER_ROLES.USER);
      } else {
        setRole(null);
      }

      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      user: firebaseUser,
      role,
      isAuthenticated: Boolean(firebaseUser),
      isAdmin: role === USER_ROLES.ADMIN,
      isRealtor: role === USER_ROLES.REALTOR,
      isLoading,
    }),
    [firebaseUser, role, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
